import { isDefined } from '@sgme/fp';
import type { LanguageIsoCode } from 'locales';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { ProductVariation, ProductVariationValidSyntax } from 'models/productsReferential';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { createSelector } from 'reselect';
import { possibleRegions, type Region } from 'state/discover/page/page.model';
import type { ProductCodeType } from 'state/secondaryMarket/productSearch';
import { getIsUserInternal } from 'state/selectors/common/user';
import { isFeatureEnabled } from 'state/selectors/common/userFeatures';
import type { AppState } from 'state/state.model';
import { isUserInternal } from 'state/user/user.selector';
import { WorkspaceWithProductSelector } from 'state/workspaces/workspaces/workspaces.model';

import type {
  BatchWorkspaceVersion,
  DisplayPrecisionValues,
  LandingPage,
  Notifications,
  ProductLayoutOverride,
  ProductVariationKey,
  QuoteWorkspaceVersion,
  SgBsTheme,
  UserPreferences,
} from './userPreferences.model';
import type { UserPreferencesState } from './userPreferences.types';

function selectUserPreferencesState(state: AppState): UserPreferencesState {
  return state.userPreferences;
}

export function areUserPreferencesInError(state: AppState) {
  return selectUserPreferencesState(state)?.status === 'error';
}

export function areUserPreferencesReady(state: AppState) {
  return selectUserPreferencesState(state)?.status === 'ready';
}

export function areUserPreferencesLoading(state: AppState) {
  return selectUserPreferencesState(state)?.status === 'loading';
}

export function isUserPreferencesDarkMode(state: AppState): boolean {
  const isDarkMode = selectUserPreferencesState(state)?.isDarkMode;
  return isDarkMode !== undefined ? isDarkMode : false;
}

const darkTheme: SgBsTheme = 'dark';
const lightTheme: SgBsTheme = 'light';
export function getUserPreferencesTheme(state: AppState): SgBsTheme {
  return isUserPreferencesDarkMode(state) ? darkTheme : lightTheme;
}

export function getUserPreferencesNotifications(state: AppState): Notifications | undefined {
  return selectUserPreferencesState(state)?.notifications;
}

const defaultLandingPage: LandingPage = 'QuoteWorkspace';

export function getUserPreferencesLandingPage(state: AppState): LandingPage | undefined {
  if (areUserPreferencesLoading(state)) {
    return undefined;
  }
  if (areUserPreferencesReady(state)) {
    const landingPage = selectUserPreferencesState(state)?.landingPage;
    switch (landingPage) {
      case 'QuoteWorkspace':
      case 'WelcomePage':
        return landingPage;
      default:
        // Invalid or missing values get defaulted
        return defaultLandingPage;
    }
  }
  if (areUserPreferencesInError(state)) {
    return defaultLandingPage;
  }
}

export function getUserPreferencesDiscoverPageRegion(state: AppState): Region | undefined {
  return selectUserPreferencesState(state)?.discoverPageRegion;
}

export function getUserPreferencesAvailableDiscoverPageRegions(state: AppState): readonly Region[] {
  return selectUserPreferencesState(state)?.availableDiscoverPageRegions ?? possibleRegions;
}

export function getUserPreferencesQuoteWorkspaceVersion(state: AppState): QuoteWorkspaceVersion {
  if (isFeatureEnabled(state, 'essentialWorkspaceParis')) {
    return 'Advanced';
  }
  const actualQuoteWorkspaceVersion = selectUserPreferencesState(state)?.quoteWorkspaceVersion;
  if (actualQuoteWorkspaceVersion !== undefined) {
    return actualQuoteWorkspaceVersion;
  }
  const isInternalUser = getIsUserInternal(state);
  return isInternalUser ? 'Advanced' : 'Standard';
}

export function getUserPreferencesBatchWorkspaceVersion(state: AppState): BatchWorkspaceVersion {
  const actualBatchWorkspaceVersion = selectUserPreferencesState(state)?.batchWorkspaceVersion;
  if (actualBatchWorkspaceVersion !== undefined) {
    return actualBatchWorkspaceVersion;
  }
  const isInternalUser = getIsUserInternal(state);
  return isInternalUser ? 'Advanced' : 'Standard';
}

const defaultDefaultLanguage = 'en';

export function getUserPreferencesLanguage(state: AppState): LanguageIsoCode {
  const language = selectUserPreferencesState(state)?.languageIsoCode;
  return language ?? defaultDefaultLanguage;
}

export function isUserPreferencesPrivateMode(state: AppState): boolean {
  const isPrivateMode = selectUserPreferencesState(state)?.defaultFieldValues?.isPrivateMode;
  return isPrivateMode !== undefined ? isPrivateMode : true;
}

export function getUserPreferencesSelectedOnBehalfClient(state: AppState): ISalesCounterpart | undefined {
  return selectUserPreferencesState(state)?.defaultFieldValues?.selectedOnBehalfClient;
}

export function getUserPreferencesXMargin(state: AppState): number | undefined {
  const xMargin = selectUserPreferencesState(state)?.defaultFieldValues?.xMargin;
  if (isDefined(xMargin) && xMargin > 0) {
    return xMargin;
  }
  return undefined;
}

export function getUserPreferencesProductType(state: AppState): string | undefined {
  const userPreferencesState = selectUserPreferencesState(state);
  const productType = userPreferencesState?.defaultFieldValues?.productType;
  return productType;
}

export function getUserPreferencesProductSubtype(state: AppState): string | undefined {
  const userPreferencesState = selectUserPreferencesState(state);
  const productSubtype = userPreferencesState?.defaultFieldValues?.productSubtype;
  return productSubtype;
}

export function getUserPreferencesWrapper(state: AppState): string | undefined {
  const userPreferencesState = selectUserPreferencesState(state);
  const wrapper = userPreferencesState?.defaultFieldValues?.wrapper;
  return wrapper;
}

export function getUserPreferencesCurrency(state: AppState): string | undefined {
  const userPreferencesState = selectUserPreferencesState(state);
  return userPreferencesState?.defaultFieldValues?.currency;
}

export function getUserPreferencesAvailableProductTypes(state: AppState): string[] {
  const productTypes = selectUserPreferencesState(state).availableSpProducts?.map(product => product.productType);
  return Array.from(new Set(productTypes || []));
}

export function getUserPreferencesAvailableProductSubtypes(state: AppState): string[] {
  const productType = getUserPreferencesProductType(state);

  const productSubtypes = selectUserPreferencesState(state)
    .availableSpProducts?.filter(product => product.productType === productType)
    .map(product => product.productSubType);

  return Array.from(new Set(productSubtypes || []));
}

export function getUserPreferencesAvailableWrappers(state: AppState): string[] {
  const productSubtype = getUserPreferencesProductSubtype(state);

  const wrappers = selectUserPreferencesState(state)
    .availableSpProducts?.filter(product => product.productSubType === productSubtype)
    .map(product => product.wrapper);

  return Array.from(new Set(wrappers || []));
}

export function getUserPreferencesAvailableCurrencies(state: AppState): ReadonlyArray<string | null> {
  const currencies = selectUserPreferencesState(state).availableCurrencies;
  return Array.from(new Set(currencies || []));
}

export function getUserPreferencesAll(state: AppState): UserPreferences {
  const { status: _, ...preferences } = selectUserPreferencesState(state);
  return preferences as UserPreferences;
}

export function getUserPreferencesGreeksPrecision(state: AppState): number | undefined {
  return selectUserPreferencesState(state)?.precision?.greeks;
}

export function getUserPreferencesUnderlyingCodeType(
  state: AppState,
  defaultCodeType: UnderlyingCodeType = 'Bloomberg',
): UnderlyingCodeType {
  return selectUserPreferencesState(state)?.underlyingCodeType ?? defaultCodeType;
}

export const getUserPreferencesPrecisions = (state: AppState): DisplayPrecisionValues => {
  const isInternal = isUserInternal(state.user);
  const precision = selectUserPreferencesState(state).precision;
  if (!isInternal) {
    return { greeks: precision?.greeks };
  }
  return precision;
};
export function getUserPreferencesFeesPrecision(state: AppState): number | undefined {
  return getUserPreferencesPrecisions(state)?.fees;
}

export function getUserPreferencesMetricsPrecision(state: AppState): number | undefined {
  return getUserPreferencesPrecisions(state)?.metrics;
}

export function getUserPreferencesBackTestPrecision(state: AppState): number | undefined {
  return getUserPreferencesPrecisions(state)?.backtest;
}

export function getUserPreferencesSecondaryMarketProductSearchCodeType(state: AppState): ProductCodeType {
  return selectUserPreferencesState(state)?.productCodeType ?? 'ISIN';
}

export function getUserPreferencesSecondaryMarketCloseTabWhenOrderPlacedActivated(state: AppState): boolean {
  return selectUserPreferencesState(state)?.closeTabWhenLiveTradeCpOrderIsPlacedActivated ?? false;
}

export const getUserPreferencesPinnedProductsVariations = (workspace: WorkspaceWithProductSelector) =>
  createSelector(selectUserPreferencesState, state =>
    workspace === 'essential' ? (state.essentialPinnedProductVariations ?? []) : (state.pinnedProductVariations ?? []),
  );

export const getUserPreferencesProductVariation: (state: AppState) => Partial<ProductVariation> | undefined =
  createSelector(
    [getUserPreferencesProductType, getUserPreferencesProductSubtype, getUserPreferencesWrapper],
    (productType, productSubType, wrapper) => ({ productType, productSubType, wrapper }),
  );

export const getUserPreferenceShowBatchShortcutCheatsheet = createSelector(
  selectUserPreferencesState,
  state => state.showBatchShortcutCheatsheet ?? true,
);

const emptyEssentialExpandedBlocks: string[] = [];
export function selectUserPreferencesEssentialExpandedBlocks(
  state: AppState,
  productType: string,
  productSubType: string,
): string[] {
  return state.userPreferences.essentialExpandedBlocks?.[productType]?.[productSubType] ?? emptyEssentialExpandedBlocks;
}

export const getUserPreferenceProductLayoutPreferences = (state: AppState) => {
  return selectUserPreferencesState(state)?.productLayoutPreferences;
};

export const getProductVariationKey = (variation: ProductVariationValidSyntax): ProductVariationKey =>
  `${variation.productType.trim().toLowerCase()}:${variation.productSubtype.trim().toLowerCase()}`;

export function getUserPreferenceProductLayoutOverride(
  state: AppState,
  productVariation: ProductVariationValidSyntax | undefined,
): ProductLayoutOverride | undefined {
  if (!productVariation?.productType || !productVariation.wrapper) {
    return undefined;
  }

  if (!productVariation.productSubtype) {
    return undefined;
  }

  const productLayoutPreferences = getUserPreferenceProductLayoutPreferences(state);
  return productLayoutPreferences?.layoutsByProduct[
    getProductVariationKey({
      productType: productVariation.productType,
      productSubtype: productVariation.productSubtype,
      wrapper: productVariation.wrapper,
    })
  ];
}
